// Door-to-frame gap diagram + ordered measurement rows for the report.
//
// The numbers match the inspector's gap diagram:
//   • Single leaf: 1–12  (1 = Lock-side top … 12 = Top lock-side)
//   • Double leaf: 1–15  (centre meeting edge 1–3, leaf-2 hinge 4–6,
//     leaf-2 top 7–9, leaf-1 top 10–12, leaf-1 hinge 13–15)
// Under-door / threshold points are NOT numbered on the diagram; on a
// single leaf they are points 4/5/6 and are skipped entirely when a
// dropdown seal is already fitted.

export interface GapRow { num: number; pos: string; key: string; }

// ── Single leaf — 12-point ───────────────────────────────────────────
const SINGLE_ROWS: GapRow[] = [
  { num: 1, pos: 'Lock side — top', key: 'lock_top' },
  { num: 2, pos: 'Lock side — middle', key: 'lock_mid' },
  { num: 3, pos: 'Lock side — bottom', key: 'lock_bot' },
  { num: 4, pos: 'Bottom — lock side', key: 'bot_lock' },
  { num: 5, pos: 'Bottom — middle', key: 'bot_mid' },
  { num: 6, pos: 'Bottom — hinge side', key: 'bot_hinge' },
  { num: 7, pos: 'Hinge side — bottom', key: 'hinge_bot' },
  { num: 8, pos: 'Hinge side — middle', key: 'hinge_mid' },
  { num: 9, pos: 'Hinge side — top', key: 'hinge_top' },
  { num: 10, pos: 'Top — hinge side', key: 'top_hinge' },
  { num: 11, pos: 'Top — middle', key: 'top_mid' },
  { num: 12, pos: 'Top — lock side', key: 'top_lock' },
];

// ── Double leaf — 15 numbered points ─────────────────────────────────
const DOUBLE_ROWS: GapRow[] = [
  { num: 1, pos: 'Leaf 1 meeting edge — top', key: 'lock_top' },
  { num: 2, pos: 'Leaf 1 meeting edge — middle', key: 'lock_mid' },
  { num: 3, pos: 'Leaf 1 meeting edge — bottom', key: 'lock_bot' },
  { num: 4, pos: 'Leaf 2 hinge side — bottom', key: 'd2_hinge_bot' },
  { num: 5, pos: 'Leaf 2 hinge side — middle', key: 'd2_hinge_mid' },
  { num: 6, pos: 'Leaf 2 hinge side — top', key: 'd2_hinge_top' },
  { num: 7, pos: 'Leaf 2 top — hinge side', key: 'd2_top_hinge' },
  { num: 8, pos: 'Leaf 2 top — middle', key: 'd2_top_mid' },
  { num: 9, pos: 'Leaf 2 top — meeting edge', key: 'd2_top_lock' },
  { num: 10, pos: 'Leaf 1 top — meeting edge', key: 'top_lock' },
  { num: 11, pos: 'Leaf 1 top — middle', key: 'top_mid' },
  { num: 12, pos: 'Leaf 1 top — hinge side', key: 'top_hinge' },
  { num: 13, pos: 'Leaf 1 hinge side — top', key: 'hinge_top' },
  { num: 14, pos: 'Leaf 1 hinge side — middle', key: 'hinge_mid' },
  { num: 15, pos: 'Leaf 1 hinge side — bottom', key: 'hinge_bot' },
];

// Under-door / threshold points (double leaf). Numbered 16–21, left to
// right across the pair. Only shown when NO dropdown seal is fitted.
const DOUBLE_UNDER_DOOR: GapRow[] = [
  { num: 16, pos: 'Leaf 1 under-door — hinge side', key: 'bot_hinge' },
  { num: 17, pos: 'Leaf 1 under-door — middle', key: 'bot_mid' },
  { num: 18, pos: 'Leaf 1 under-door — meeting edge', key: 'bot_lock' },
  { num: 19, pos: 'Leaf 2 under-door — meeting edge', key: 'd2_bot_lock' },
  { num: 20, pos: 'Leaf 2 under-door — middle', key: 'd2_bot_mid' },
  { num: 21, pos: 'Leaf 2 under-door — hinge side', key: 'd2_bot_hinge' },
];

export interface DiagramOpts { double: boolean; dropdownSeal: boolean; failedNums?: Set<number>; }

// Ordered rows for the report table. All points are numbered and drawn on
// the diagram. Under-door points (single 4/5/6, double 16–21) are dropped
// when a dropdown/threshold seal is already fitted.
export function gapRowsFor(opts: { double: boolean; dropdownSeal: boolean }): { rows: GapRow[]; threshold: GapRow[] } {
  if (opts.double) {
    const rows = opts.dropdownSeal ? DOUBLE_ROWS : [...DOUBLE_ROWS, ...DOUBLE_UNDER_DOOR];
    return { rows, threshold: [] };
  }
  const rows = opts.dropdownSeal ? SINGLE_ROWS.filter((r) => ![4, 5, 6].includes(r.num)) : SINGLE_ROWS;
  return { rows, threshold: [] };
}

// A numbered badge (circle + centred number). Red when that point failed.
function badge(cx: number, cy: number, n: number, failed: boolean): string {
  const fill = failed ? '#EF4444' : '#0F2B3D';
  return (
    `<circle cx="${cx}" cy="${cy}" r="15" fill="${fill}" />` +
    `<text x="${cx}" y="${cy}" dy=".35em" text-anchor="middle" font-family="Arial, sans-serif" font-size="15" font-weight="700" fill="#ffffff">${n}</text>`
  );
}

const LINE = '#0F2B3D';
const FRAME_FILL = '#E2E8F0';
const LEAF_FILL = '#ffffff';
const HW = '#334155'; // handle / hinge hardware

function singleSvg(opts: DiagramOpts): string {
  const f = opts.failedNums || new Set<number>();
  const seal = opts.dropdownSeal;
  const hinges = [140, 290, 440].map((y) => `<rect x="356" y="${y - 17}" width="9" height="34" rx="2" fill="${HW}" />`).join('');
  const handle = `<rect x="104" y="291" width="44" height="7" rx="3.5" fill="${HW}" /><circle cx="110" cy="314" r="6" fill="${HW}" />`;
  const badges = [
    badge(42, 110, 1, f.has(1)), badge(42, 290, 2, f.has(2)), badge(42, 470, 3, f.has(3)),
    badge(418, 470, 7, f.has(7)), badge(418, 290, 8, f.has(8)), badge(418, 110, 9, f.has(9)),
    badge(320, 32, 10, f.has(10)), badge(230, 32, 11, f.has(11)), badge(140, 32, 12, f.has(12)),
  ];
  if (!seal) {
    badges.push(badge(140, 548, 4, f.has(4)), badge(230, 548, 5, f.has(5)), badge(320, 548, 6, f.has(6)));
  }
  const sealNote = seal
    ? `<text x="230" y="548" dy=".35em" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" fill="#6B7280">Dropdown seal fitted — under-door not measured</text>`
    : '';
  return `<svg viewBox="0 0 460 580" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:auto">
    <rect x="70" y="55" width="320" height="470" rx="5" fill="${FRAME_FILL}" stroke="${LINE}" stroke-width="3" />
    <rect x="92" y="77" width="276" height="426" rx="2" fill="${LEAF_FILL}" stroke="${LINE}" stroke-width="2" />
    ${hinges}${handle}${sealNote}${badges.join('')}
  </svg>`;
}

function doubleSvg(opts: DiagramOpts): string {
  const f = opts.failedNums || new Set<number>();
  const underDoor = !opts.dropdownSeal;
  const lHinges = [130, 270, 410].map((y) => `<rect x="86" y="${y - 16}" width="8" height="32" rx="2" fill="${HW}" />`).join('');
  const rHinges = [130, 270, 410].map((y) => `<rect x="466" y="${y - 16}" width="8" height="32" rx="2" fill="${HW}" />`).join('');
  const handles = `<rect x="214" y="266" width="34" height="7" rx="3.5" fill="${HW}" /><rect x="312" y="266" width="34" height="7" rx="3.5" fill="${HW}" />`;
  const badges = [
    // leaf 1 hinge (outer left)
    badge(35, 110, 13, f.has(13)), badge(35, 270, 14, f.has(14)), badge(35, 430, 15, f.has(15)),
    // leaf 1 top
    badge(110, 35, 12, f.has(12)), badge(172, 35, 11, f.has(11)), badge(234, 35, 10, f.has(10)),
    // centre meeting edge
    badge(280, 110, 1, f.has(1)), badge(280, 270, 2, f.has(2)), badge(280, 430, 3, f.has(3)),
    // leaf 2 top
    badge(326, 35, 9, f.has(9)), badge(388, 35, 8, f.has(8)), badge(450, 35, 7, f.has(7)),
    // leaf 2 hinge (outer right)
    badge(525, 110, 6, f.has(6)), badge(525, 270, 5, f.has(5)), badge(525, 430, 4, f.has(4)),
  ];
  if (underDoor) {
    badges.push(
      badge(112, 505, 16, f.has(16)), badge(172, 505, 17, f.has(17)), badge(232, 505, 18, f.has(18)),
      badge(328, 505, 19, f.has(19)), badge(388, 505, 20, f.has(20)), badge(448, 505, 21, f.has(21)),
    );
  }
  const sealNote = underDoor ? '' :
    `<text x="280" y="505" dy=".35em" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" fill="#6B7280">Dropdown seal fitted — under-door not measured</text>`;
  const vbH = 540;
  return `<svg viewBox="0 0 560 ${vbH}" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:auto">
    <rect x="60" y="55" width="440" height="430" rx="5" fill="${FRAME_FILL}" stroke="${LINE}" stroke-width="3" />
    <rect x="82" y="77" width="180" height="386" rx="2" fill="${LEAF_FILL}" stroke="${LINE}" stroke-width="2" />
    <rect x="298" y="77" width="180" height="386" rx="2" fill="${LEAF_FILL}" stroke="${LINE}" stroke-width="2" />
    ${lHinges}${rHinges}${handles}${sealNote}${badges.join('')}
  </svg>`;
}

export function buildDoorDiagramSvg(opts: DiagramOpts): string {
  return opts.double ? doubleSvg(opts) : singleSvg(opts);
}
